/* ==========================================================================
   Banner Slide
   Web Component: <banner-slide>
   Hidratação 'eager' — o listener de carousel:init precisa estar pronto
   antes do <carousel-component> (tier 'visible') emitir o evento.
   ========================================================================== */

import { BaseComponent as RkComponent } from '@theme/base-component';

class RkBannerSlide extends RkComponent {
    /** @type {MediaQueryList | null} */
    #mql = null;

    /** @type {(() => void) | null} */
    #onBreakpointChange = null;

    /** @type {(() => void) | null} */
    #onSlideChange = null;

    /** @type {any} */
    swiper = null;

    setup() {
        this.#mql = window.matchMedia('(max-width: 749px)');

        this.addEventListener('carousel:init', (/** @type {Event} */ e) => {
            const detail = /** @type {CustomEvent} */ (e).detail;
            this.swiper = detail.swiper;
            this.#onCarouselReady();
        });
    }

    teardown() {
        if (this.#mql && this.#onBreakpointChange) {
            this.#mql.removeEventListener('change', this.#onBreakpointChange);
        }
        if (this.swiper && this.#onSlideChange) {
            this.swiper.off('slideChangeTransitionStart', this.#onSlideChange);
        }
        this.#mql = null;
        this.#onBreakpointChange = null;
        this.#onSlideChange = null;
        this.swiper = null;
    }

    #onCarouselReady() {
        const swiper = this.swiper;
        if (!swiper || !this.#mql) return;

        if (swiper.params.autoplay?.delay) {
            this.style.setProperty(
                '--rk-banner-autoplay-delay',
                `${swiper.params.autoplay.delay}ms`
            );
        }

        this.#loadSlideVideo(swiper.slides[swiper.activeIndex]);

        this.#onSlideChange = () => {
            this.#loadSlideVideo(swiper.slides[swiper.activeIndex]);
        };
        swiper.on('slideChangeTransitionStart', this.#onSlideChange);

        this.#onBreakpointChange = () => {
            const loaded = this.querySelectorAll('.banner-slider__media--video[data-loaded]');
            loaded.forEach((media) => this.#swapVideo(/** @type {HTMLElement} */ (media)));
        };
        this.#mql.addEventListener('change', this.#onBreakpointChange);
    }

    /** @param {Element | undefined} slide */
    #loadSlideVideo(slide) {
        if (!slide) return;
        const media = /** @type {HTMLElement | null} */ (
            slide.querySelector('.banner-slider__media--video')
        );
        if (!media || media.dataset.loaded) return;

        this.#swapVideo(media);
        media.dataset.loaded = 'true';
    }

    /** @param {HTMLElement} media */
    #swapVideo(media) {
        const existing = media.querySelector('.banner-slider__video');
        if (existing) existing.remove();

        const device = this.#mql?.matches ? 'mobile' : 'desktop';
        const tpl = /** @type {HTMLTemplateElement | null} */ (
            media.querySelector(`.banner-slider__video-tpl[data-device="${device}"]`) ||
            media.querySelector('.banner-slider__video-tpl[data-device="desktop"]')
        );
        if (!tpl) return;

        media.appendChild(tpl.content.cloneNode(true));

        if (this.swiper?.params.autoHeight) {
            const inserted = media.querySelector('.banner-slider__video');
            if (!inserted) return;
            const event = inserted.tagName === 'IFRAME' ? 'load' : 'loadedmetadata';
            inserted.addEventListener(event, () => this.swiper?.updateAutoHeight(), { once: true });
        }
    }
}

if (!customElements.get('banner-slide')) {
    customElements.define('banner-slide', RkBannerSlide);
}
